import React, { useState, useEffect, useRef } from 'react';
import { Table, Input, Tabs, Button, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { EyeOutlined, CopyOutlined } from '@ant-design/icons';
import { productsService } from "../../services/ProductsService";
import Menu from '../../components/Menu/Menu';
import './Products.scss';
import MobileBottomMenu from '../../components/MobileBottomMenu/MobileBottomMenu';
import Header from '../../components/Header/Header';
import Barcode from 'react-barcode';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { ScanOutlined } from '@ant-design/icons';
import BarcodeScannerOverlay from '../../components/BarcodeScannerOverlay/BarcodeScannerOverlay';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const { Search } = Input;
const { TabPane } = Tabs;

const Products = () => {
  const [barcodeModalVisible, setBarcodeModalVisible] = useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState('');
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [barcodeFound, setBarcodeFound] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState(null);
  const codeReaderRef = useRef(null);

  const checkCameraAvailability = () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return {
        available: false,
        error: 'Cameră indisponibilă. Asigură-te că aplicația rulează pe HTTPS sau localhost și că browser-ul suportă accesul la cameră.'
      };
    }
    return { available: true };
  };

  const requestCameraPermission = async () => {
    try {
      // Cerem explicit permisiunea camerei înainte de a începe scanarea
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment' // Camera din spate pe mobile
        } 
      });
      // Oprim stream-ul imediat - doar vrem să cerem permisiunea
      stream.getTracks().forEach(track => track.stop());
      return { success: true };
    } catch (error) {
      console.error('Camera permission error:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        return {
          success: false,
          error: 'Accesul la cameră a fost refuzat. Te rugăm să permți accesul la cameră în setările browser-ului sau ale aplicației.'
        };
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        return {
          success: false,
          error: 'Nu s-a găsit nicio cameră disponibilă pe acest dispozitiv.'
        };
      } else {
        return {
          success: false,
          error: 'Eroare la accesarea camerei: ' + error.message
        };
      }
    }
  };

  useEffect(() => {
    if (!scanModalOpen) {
      setCameraError(null);
      return;
    }

    const cameraCheck = checkCameraAvailability();
    if (!cameraCheck.available) {
      setCameraError(cameraCheck.error);
      return;
    }

    // Flag pentru a verifica dacă componenta este încă montată
    let isMounted = true;
    let timeoutId = null;
    let isProcessingBarcode = false;

    // BrowserMultiFormatReader suportă implicit rotirea barcode-urilor
    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;

    const videoElement = document.getElementById('barcode-video');
    if (!videoElement) {
      setCameraError('Element video nu a fost găsit.');
      return;
    }

    // Funcție pentru a porni scanarea
    const startScanning = async () => {
      try {
        // Cerem explicit permisiunea camerei
        const permissionResult = await requestCameraPermission();
        
        if (!isMounted) return;
        
        if (!permissionResult.success) {
          setCameraError(permissionResult.error);
          return;
        }

        // Folosim un timeout mic pentru a ne asigura că DOM-ul este gata
        timeoutId = setTimeout(() => {
          if (!isMounted) return;
          
          setIsScanning(true);
          console.log('Starting barcode scanner...');
          
          codeReader.decodeFromVideoDevice(
            null,
            videoElement,
            (result, error) => {
              if (!isMounted || isProcessingBarcode) return;

              if (error) {
                // Ignorăm erorile normale de scanare
                if (error.name === 'NotFoundException' || 
                    error.message?.includes('No MultiFormat Readers') ||
                    error.message?.includes('not found')) {
                  return;
                }
                
                console.error('Barcode scan error:', error);
                if (!isMounted) return;
                
                setIsScanning(false);
                if (error.name === 'NotAllowedError') {
                  setCameraError('Accesul la cameră a fost refuzat. Te rugăm să permți accesul la cameră în setările browser-ului.');
                } else if (error.name === 'NotFoundError') {
                  setCameraError('Nu s-a găsit nicio cameră disponibilă.');
                } else {
                  setCameraError('Eroare la accesarea camerei: ' + error.message);
                }
                return;
              }
              
              if (result) {
                isProcessingBarcode = true;
                
                const barcode = result.getText();
                console.log('✅ Scanned barcode successfully:', barcode);
                
                // Oprim scanarea imediat
                if (codeReader && typeof codeReader.reset === 'function') {
                  codeReader.reset();
                }
                
                if (!isMounted) return;
                
                setIsScanning(false);
                setScannedBarcode(barcode);
                setBarcodeFound(true);
                
                // Așteptăm 1 secundă pentru feedback verde
                setTimeout(() => {
                  if (!isMounted) return;
                  
                  setBarcodeFound(false);
                  setIsSearching(true);
                  handleSearch(barcode);
                  
                  // După căutare, închidem modalul
                  setTimeout(() => {
                    if (!isMounted) return;
                    
                    setIsSearching(false);
                    setScanModalOpen(false);
                    setCameraError(null);
                    setScannedBarcode(null);
                  }, 500);
                }, 1000);
              }
            }
          ).catch((error) => {
            if (!isMounted) return;
            
            console.error('Error starting camera:', error);
            setIsScanning(false);
            
            if (error.name === 'NotAllowedError') {
              setCameraError('Accesul la cameră a fost refuzat. Te rugăm să permți accesul la cameră în setările browser-ului.');
            } else if (error.name === 'NotFoundError') {
              setCameraError('Nu s-a găsit nicio cameră disponibilă.');
            } else if (!error.message?.includes('No MultiFormat Readers')) {
              setCameraError('Eroare la accesarea camerei: ' + error.message);
            }
          });
        }, 100);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error in startScanning:', error);
        setCameraError('Eroare la pornirea camerei: ' + error.message);
      }
    };

    startScanning();

    // Cleanup function - acum la nivelul corect
    return () => {
      isMounted = false;
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (codeReaderRef.current && typeof codeReaderRef.current.reset === 'function') {
        try {
          codeReaderRef.current.reset();
        } catch (error) {
          console.warn('Error resetting code reader:', error);
        }
      }
    };
  }, [scanModalOpen]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Cod de bare',
      dataIndex: 'barcode',
      key: 'barcode',
      render: (barcode) => (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}>
          <span style={{ color: '#000' }}>{barcode}</span>

          <div style={{ display: 'flex', gap: 8 }}>

            {/* COPY */}
            <Button
              type="link"
              onClick={() => {
                navigator.clipboard.writeText(barcode);
                toast.success('Codul de bare a fost copiat!');
              }}
              style={{ padding: 0, height: 'auto', lineHeight: 1 }}
            >
              <span
                style={{
                  backgroundColor: 'gray',
                  borderRadius: '50%',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                }}
              >
                <CopyOutlined style={{ color: '#fff', fontSize: '14px' }} />
              </span>
            </Button>

            <Button
              type="link"
              onClick={() => {
                setSelectedBarcode(barcode);
                setBarcodeModalVisible(true);
              }}
              style={{ padding: 0, height: 'auto', lineHeight: 1 }}
            >
              <span
                style={{
                  backgroundColor: '#1890ff',
                  borderRadius: '50%',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                }}
              >
                <EyeOutlined style={{ color: '#fff', fontSize: '14px' }} />
              </span>
            </Button>

          </div>
        </div>
      ),
    },
    {
      title: 'Internal number',
      dataIndex: 'internal_number',
      key: 'internal_number',
    },
    {
      title: 'Measurement Unit',
      dataIndex: 'measurement_unit',
      key: 'measurement_unit',
    },
    {
      title: 'Selling price',
      dataIndex: 'selling_price',
      key: 'selling_price',
    },
    {
      title: 'PA TVA',
      dataIndex: 'pa_tva',
      key: 'pa_tva',
    },
    {
      title: 'Vezi produs',
      key: 'view',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/product-details/${record.id}`)}
          style={{ padding: 0, height: 'auto' }}
        >
          <EyeOutlined style={{ fontSize: 18 }} />
        </Button>
      ),
    }
  ];
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    fetchProducts();
  }, [navigate]);

  const fetchProducts = (page = 1, search = '') => {
    const normalizedSearch = search.trim();
    const request = normalizedSearch
      ? productsService.searchProduct(normalizedSearch, page)
      : productsService.getProducts(page);

    request
      .then(response => {
        if (response.status === 401) {
          navigate("/login");
        } else {
          setProducts(response.data.products);
          setPagination(response.data.pagination);
        }
      })
      .catch(error => {
        if (error.response && error.response.status === 401) {
          navigate("/login");
        } else {
          console.error('Error fetching data:', error);
        }
      });
  };

  const handlePageClick = (data) => {
    fetchProducts(data.selected + 1, searchQuery);
  };

  const handleSearch = (value = '') => {
    const nextQuery = value ?? '';
    setSearchQuery(nextQuery);
    fetchProducts(1, nextQuery);
  };

  const dataSource = products
    ? Object.keys(products).map(key => ({
      ...products[key],
    }))
    : [];

  return (
    <div className='standard products list'>
      <Header />

      <div className='standard__container'>
        <Menu />
        <div className='standard__wrapper'>
          <div style={{ padding: 24, minHeight: 280 }}>
            <h2 className='standard__page-title'>Listă produse</h2>
            <h4 className='standard__page-info'>
              În această pagină poți vizualiza și edita produsele magazinului. Momentan, în această etapă a proiectului, editările în listele de produse trebuie realizate exclusiv de către higher management.
            </h4>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
                <TabPane tab={`Număr produse (${dataSource.length})`} key="1" />
                <TabPane tab="Filtre" key="2" />
              </Tabs>
              <div style={{ display: 'flex', gap: 8 }}>
                <Search
                  placeholder="Caută produs"
                  onSearch={handleSearch}
                  onChange={(event) => handleSearch(event.target.value)}
                  enterButton
                  allowClear
                  value={searchQuery}
                  style={{ width: 260 }}
                />

                <Button
                  icon={<ScanOutlined />}
                  onClick={() => setScanModalOpen(true)}
                />
              </div>
            </div>

            <div className="list__table-wrapper">
              <div className='list__table-content'>
                <Table
                  dataSource={dataSource}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                />
              </div>
            </div>

            {pagination && pagination.last_page && (
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                breakClassName={'break-me'}
                pageCount={pagination.last_page}
                marginPagesDisplayed={2}
                pageRangeDisplayed={1}
                onPageChange={handlePageClick}
                containerClassName={'pagination'}
                subContainerClassName={'pages pagination'}
                activeClassName={'active'}
              />
            )}
          </div>
        </div>
      </div>
      <Modal
        open={barcodeModalVisible}
        onCancel={() => setBarcodeModalVisible(false)}
        footer={null}
      >
        <div style={{ textAlign: 'center' }}>
          <Barcode value={selectedBarcode} />
        </div>
      </Modal>
      <Modal
        open={scanModalOpen}
        footer={null}
        onCancel={() => {
          setScanModalOpen(false);
          setCameraError(null);
          setIsScanning(false);
          setBarcodeFound(false);
          setIsSearching(false);
          setScannedBarcode(null);
        }}
        title="Scanează cod de bare"
        width="100%"
        style={{ top: 0 }}
        bodyStyle={{ padding: 0 }}
      >
        {cameraError ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ color: '#ff4d4f', marginBottom: 16 }}>{cameraError}</p>
            <p style={{ fontSize: 12, color: '#666' }}>
              Notă: Funcționalitatea de scanare necesită HTTPS sau localhost pentru a accesa camera.
            </p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <video
              id="barcode-video"
              style={{
                width: '100%',
                height: '80vh',
                objectFit: 'cover'
              }}
            />
            <BarcodeScannerOverlay 
              isScanning={isScanning} 
              barcodeFound={barcodeFound}
              isSearching={isSearching}
              scannedBarcode={scannedBarcode}
            />
          </div>
        )}
      </Modal>
      <MobileBottomMenu />
    </div>
  );
};

export default Products;